import {
  ReactNode,
  createContext,
  useCallback,
  useEffect,
  useState,
} from 'react'
import {
  FetchNextPageOptions,
  InfiniteData,
  InfiniteQueryObserverResult,
} from '@tanstack/react-query'
import { useDebounce } from '../hooks/useDebounce'
import { useSellers } from '../hooks/useSellers'
import { SellerResponse } from '../services/sellersService/seller'

interface FiltersType {
  sellerIds?: string
  teamIds?: string
  blockedUsers?: string
}

interface SellersContextValue {
  sellers: {
    isLoading: boolean
    isFetching: boolean
    data: InfiniteData<SellerResponse, unknown> | undefined
    fetchNextPage: (
      options?: FetchNextPageOptions | undefined,
    ) => Promise<
      InfiniteQueryObserverResult<InfiniteData<SellerResponse, unknown>, Error>
    >
    hasNextPage: boolean
    isFetchingNextPage: boolean
  }
  setSearchValue: React.Dispatch<React.SetStateAction<string | undefined>>
  searchValue?: string
  setFilterParams: (value: FiltersType) => void
}
export const SellersContext = createContext({} as SellersContextValue)

export function SellersProvider({ children }: { children: ReactNode }) {
  const [searchValue, setSearchValue] = useState<string | undefined>(undefined)
  const debouncedSearchValue = useDebounce(500, searchValue)

  const { setParams, ...sellers } = useSellers({ initialLimit: 30 })

  const setFilterParams = (value: FiltersType) => {
    setParams(value)
  }

  const setSearch = useCallback(() => {
    setParams((state) => ({ ...state, search: debouncedSearchValue }))
  }, [debouncedSearchValue, setParams])

  useEffect(() => {
    setSearch()
  }, [setSearch])

  return (
    <SellersContext.Provider
      value={{
        sellers,
        setSearchValue,
        searchValue,
        setFilterParams,
      }}
    >
      {children}
    </SellersContext.Provider>
  )
}
